import * as React from 'react';

import { IComponentLoadingStores } from 'shared/entities/store/rootStore';
import { LoadingStage } from 'shared/entities/meta';
import { useRootStore, useUserStore } from 'stores/index';
import LoaderSpinner from 'shared/newComponents/animations/LoaderSpinner';
import {
  LoaderSpinnerColor,
  LoaderSpinnerSize
} from 'shared/newEntities/components/LoaderSpinner';

import './loadGlobalStoresHOC.modules.scss';

const loadGlobalStoresHOC = <P extends Record<string, any>>(
  Component: React.FC<P>,
  stores: (keyof IComponentLoadingStores)[],
  loaderSize: LoaderSpinnerSize = LoaderSpinnerSize.md,
  loaderColor: LoaderSpinnerColor = LoaderSpinnerColor.blue
): React.FC<P> => {
  const WrappedComponent: React.FC<P> = (props: P) => {
    const userStore = useUserStore();
    const rootStore = useRootStore();
    const [stage, setStage] = React.useState(LoadingStage.NOT_STARTED);

    React.useEffect(() => {
      const load = async () => {
        const requiredStores = stores.filter(
          (store) => !rootStore[store].shouldBeSkipped
        );

        if (
          requiredStores.some((store) => !rootStore[store].canBeLoaded) ||
          stage !== LoadingStage.NOT_STARTED
        ) {
          return;
        }

        const notLoadedStores = requiredStores.filter(
          (store) => !rootStore[store].loaded
        );

        if (!notLoadedStores.length) {
          setStage(LoadingStage.SUCCESS);
          return;
        }

        setStage(LoadingStage.LOADING);

        const responses = await Promise.all<{ isError: boolean }>(
          notLoadedStores.map((storeKey) => {
            const store = rootStore[storeKey];

            return store.load();
          })
        );

        if (responses.some((response) => response.isError)) {
          setStage(LoadingStage.ERROR);
        } else {
          setStage(LoadingStage.SUCCESS);
        }
      };

      load();
    }, [
      stores,
      userStore.isAuthorized,
      rootStore.cabinetInitialized,
      rootStore.initialized,
      stage
    ]);

    const isLoading =
      stage === LoadingStage.NOT_STARTED || stage === LoadingStage.LOADING;

    return (
      <>
        {isLoading && (
          <div styleName="container__loader">
            <LoaderSpinner size={loaderSize} color={loaderColor} />
          </div>
        )}
        {!isLoading && <Component {...props} />}
      </>
    );
  };

  return WrappedComponent;
};

export default loadGlobalStoresHOC;
